import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Col, Dropdown, Modal, notification, Row, Skeleton, Table } from 'antd';
import dayjs from 'mixins/dayjsConfig.js';
import DotsIcon from '../../manage-reestr-item/dots.svg';
import { deleteIndex, downloadDocumentIndex, saveFile, searchIndex } from '../actions';
import AddItemModal from './modal-create';
import { BaseMetaDocument } from 'mixins/types';
import { IPollData } from '../../express-help/express-help.types';
import { localize, validatePrivileges } from 'mixins/helpers';
import { StatsExternalAdmin } from '/react4xp/parts/stats-external-admin/stats-external-admin.types';

const { confirm } = Modal;

const AdminIndexPage: FC<StatsExternalAdmin.Props> = (props: StatsExternalAdmin.Props) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isAction, setIsAction] = useState<number>();
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<IPollData[]>([]);

    const pageSize = 20;
    const [pageNumber, setPageNumber] = useState(1);
    const [total, setTotal] = useState(0);

    const items: Record<string, string> = [];

    if (validatePrivileges(props.roles, props.privileges, 'PRIV_STATISTICS_DELETE')) {
        items.push({
            label: localize('button.download', props.translations),
            key: '1',
        });
    }

    if (validatePrivileges(props.roles, props.privileges, 'PRIV_STATISTICS_DELETE')) {
        items.push({
            label: 'Удалить',
            key: '2',
        });
    }

    const columns = useMemo(() => {
        return [
            {
                title: 'ID',
                dataIndex: 'id',
                key: 'id',
            },
            {
                title: localize('stats-external-name-document', props.translations),
                dataIndex: 'nameRu',
                key: 'nameRu',
            },
            {
                title: localize('stats-external-date-creation', props.translations),
                dataIndex: 'createdDate',
                key: 'createdDate',
                render: (createdDate, row) => {
                    return <span>{dayjs(row.createdDate).format('DD.MM.YYYY HH:mm')}</span>;
                },
            },
            {
                title: localize('stats-external-date-change', props.translations),
                dataIndex: 'lastModifiedDate',
                key: 'lastModifiedDate',
                render: (createdDate, row) => {
                    return <span>{dayjs(row.lastModifiedDate).format('DD.MM.YYYY HH:mm')}</span>;
                },
            },
            {
                title: localize('user', props.translations),
                key: 'lastModifiedBy',
                render: (_, item) =>
                    item.lastModifiedLastName || item.lastModifiedFirstName
                        ? `${item.lastModifiedLastName} ${item.lastModifiedFirstName}`
                        : item.lastModifiedBy,
            },
            {
                title: localize('actions', props.translations),
                key: 'actions',
                render: (_, record, index) => (
                    <Dropdown
                        menu={{ items, onClick: ({ key }) => onActionClick(key, record, index) }}
                        trigger={['click']}
                    >
                        <Button
                            className="p-0"
                            shape="round"
                            icon={<DotsIcon />}
                            loading={isAction === index}
                        />
                    </Dropdown>
                ),
            },
        ];
    }, [isAction]);

    const onActionClick = (key: string, record: any, index: number) => {
        switch (key) {
            case '1':
                downloadObj(record.metaDocument, index);
                break;
            case '2':
                deleteObj(record.id, index);
                break;
        }
    };

    const downloadObj = (record: BaseMetaDocument, index: number) => {
        confirm({
            title: localize('stats-external-confirm-download', props.translations),
            onOk() {
                setIsAction(index);

                downloadDocumentIndex(record.fileUid)
                    .then((value) => {
                        saveFile(value.data, value.headers['content-type'], record.fileName);
                        notification['success']({
                            message: localize(
                                'stats-external-uploaded-successfully',
                                props.translations
                            ),
                        });
                    })
                    .finally(() => setIsAction(null));
            },
        });
    };

    const deleteObj = (id: number, index: number) => {
        confirm({
            title: 'Подтвердите действия удаления данных',
            onOk() {
                setIsAction(index);

                deleteIndex(id)
                    .then(() => {
                        notification['success']({
                            message: 'Данные успешно удалены!',
                        });
                        getSearchIndex();
                    })
                    .finally(() => setIsAction(null));
            },
        });
    };

    const handleOk = () => {
        getSearchIndex();
        setIsModalOpen(false);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const getSearchIndex = () => {
        setIsLoading(true);

        searchIndex({
            page: pageNumber - 1,
            size: pageSize,
        })
            .then((value) => {
                setData(value.data.content);
                setTotal(value.data.totalElements);
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        getSearchIndex();
    }, [pageNumber]);

    const handlePageNumberChange = (value: any) => {
        setPageNumber(value.current);
    };

    return (
        <div className="stats-external-trade">
            <Row
                gutter={{ xs: 8, md: 10, lg: 10 }}
                justify="space-between"
                align="middle"
                className="stats-external-trade__row"
            >
                <Col span={12}>
                    {total ? (
                        <div>
                            {localize('stats-external-number-uploaded', props.translations)} {total}
                        </div>
                    ) : (
                        <Skeleton
                            active
                            title={false}
                            paragraph={{ rows: 1 }}
                        />
                    )}
                </Col>
                <Col>
                    {validatePrivileges(
                        props.roles,
                        props.privileges,
                        'PRIV_STATISTICS_UPLOAD'
                    ) && (
                        <Button
                            className="admin"
                            type="primary"
                            onClick={() => setIsModalOpen(true)}
                        >
                            {localize('add', props.translations)}
                        </Button>
                    )}
                </Col>
            </Row>

            <Table
                dataSource={data}
                columns={columns}
                loading={isLoading}
                pagination={{
                    current: pageNumber,
                    pageSize,
                    total,
                    showSizeChanger: false,
                }}
                scroll={{
                    x: true,
                }}
                onChange={handlePageNumberChange}
            />

            <AddItemModal
                isModalOpen={isModalOpen}
                category={'INDEX'}
                handleOk={handleOk}
                handleCancel={handleCancel}
                translations={props.translations}
            />
        </div>
    );
};

export default AdminIndexPage;
